import { ErrorAlert } from "@components/ErrorAlert";
import { queriesEngagements, queriesRequirementDefinitions } from "@query";
import {
  useWSModal,
  WSActions,
  WSDivider,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  CreateRequirementDefinitionRequest,
  RequirementDefinition
} from "@wingspanhq/wingspan-api-sdk";

type ConfirmCreateRequirementModalProps = {
  onClose: () => void;
  onSuccess: (requirement: RequirementDefinition) => void;
  engagementId?: string;
} & CreateRequirementDefinitionRequest;

const ConfirmCreateRequirementModal: React.FC<
  ConfirmCreateRequirementModalProps
> = ({ onClose, engagementId, onSuccess, ...requirementRequest }) => {
  const [createRequirement, createRequirementMeta] =
    queriesRequirementDefinitions.createRequirementDefinition.useMutation();
  const [addRequirementToEngagement, addRequirementToEngagementMeta] =
    queriesEngagements.addEngagementRequirement.useMutation();
  return (
    <WSList gap="XL">
      <WSInfoBox>
        <WSText.ParagraphXs mb="XS" weight="medium">
          Confirm requirement creation
        </WSText.ParagraphXs>

        <WSText.ParagraphXs weight="book">
          Once a requirement is created, it cannot be edited.
          <br />
          If the requirement needs to be changed, a new version must be created
          and configured.
          <br />
          <br />
          Existing requirements can be previewed, attached, or removed from
          engagement type at any time.
        </WSText.ParagraphXs>
      </WSInfoBox>

      <WSDivider />

      <ErrorAlert
        context="Other"
        error={
          createRequirementMeta.error || addRequirementToEngagementMeta.error
        }
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Primary",
            label: "Create requirement",
            loading:
              createRequirementMeta.isLoading ||
              addRequirementToEngagementMeta.isLoading,
            name: "createRequirementDefinition",
            onClick: async () => {
              await createRequirement(
                { createRequirementDefinitionRequest: requirementRequest },
                {
                  onSuccess: async requirement => {
                    if (engagementId) {
                      // update engagement with new requirement
                      await addRequirementToEngagement(
                        {
                          engagementId,
                          engagementRequirementCreate: {
                            requirementDefinitionId: requirement.id!
                          }
                        },
                        {
                          onSuccess: () => {
                            onSuccess(requirement);

                            onClose();
                          }
                        }
                      );
                    } else {
                      onSuccess(requirement);

                      onClose();
                    }
                  }
                }
              );
            }
          },
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: onClose
          }
        ]}
      />
    </WSList>
  );
};

export function useConfirmCreateRequirementModal() {
  return useWSModal(ConfirmCreateRequirementModal, {
    size: "S",
    title: "Create requirement"
  });
}
